import { useCallback, useEffect, useState } from 'react'
import {
  ApiError,
  api,
  can,
  refusalReason,
  tierLabel,
  type Questionnaire,
  type RiskModel,
  type TierIssuing,
  type TierProfile,
} from '../../lib/api'
import { issuingRules, saveTierIssuing, type IssuingRules } from '../../lib/workflows'
import { ErrorBox, Loading } from '../../components/DataTable'
import { Status } from '../../components/kit'
import { ReasonTip } from '../../components/vendor/ReasonTip'
import '../../styles/studio.css'
import '../../styles/sweep.css'
import '../../styles/workflows.css'

// The tier defaults: which questionnaire and scoring model each tier gets, and
// how often it is asked again.
//
// # WHY THIS IS IN SETTINGS
//
// It used to be the top of the Reassessment screen, and the product officer's
// review of 17 September 2026 (observation 3.14) asked for that screen to be a
// list of vendors instead. A tier default is a decision made once that every
// vendor in the tier then follows, which is what Settings holds. The cards and
// their rules moved here unchanged: reading needs config.read, saving needs
// config.write, exactly as before.
//
// # THE SECOND PERSON, TIER BY TIER
//
// Each card also says whether a single vendor's invitation at that tier waits
// for a second person, and lets somebody who may configure the tenant change
// it (decision 5 of the wave 3 plan; migration 0087). It is off on every tier
// until somebody turns it on. The deployment setting still applies to every
// tier on top, and the card says so rather than offering a switch that would
// change nothing.

function asApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
}

export function ReassessmentDefaults() {
  const [profiles, setProfiles] = useState<TierProfile[] | null>(null)
  const [note, setNote] = useState('')
  const [issuing, setIssuing] = useState<TierIssuing | null>(null)
  const [rules, setRules] = useState<IssuingRules | null>(null)
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([])
  const [models, setModels] = useState<RiskModel[]>([])
  const [error, setError] = useState<unknown>(null)

  const loadProfiles = useCallback(() => {
    api
      .tierProfiles()
      .then((r) => {
        setProfiles(r.tierProfiles)
        setNote(r.note)
        setIssuing(r.issuing ?? null)
        setError(null)
      })
      .catch(setError)
    // An API older than the per-tier rule answers 404, and the cards then show
    // the deployment-wide rule they always showed.
    issuingRules()
      .then(setRules)
      .catch(() => setRules(null))
  }, [])

  useEffect(loadProfiles, [loadProfiles])

  useEffect(() => {
    Promise.all([api.questionnaires(), api.riskModels()])
      .then(([q, m]) => {
        setQuestionnaires(q.questionnaires)
        setModels(m.riskModels)
      })
      .catch(setError)
  }, [])

  if (error) return <ErrorBox error={error} what="The tier defaults" />
  if (!profiles) return <Loading what="the tier defaults" />

  return (
    <section className="panel">
      {/* The API's one-line note is a hover beside the heading rather than a
          callout above the cards: it explains the system, and the cards are
          what a reader came for. */}
      <h2 className="sw-row">
        Tier defaults <ReasonTip label="About tier defaults" text={note} />
      </h2>
      <p className="muted">A vendor with its own frequency keeps it. Set that from the Reassessment list.</p>
      <div className="model-grid">
        {profiles.map((t) => (
          <TierCard
            key={t.tier}
            profile={t}
            issuing={issuing}
            rules={rules}
            questionnaires={questionnaires}
            models={models}
            onSaved={loadProfiles}
          />
        ))}
      </div>
    </section>
  )
}

// Who has to agree before an invitation reaches a vendor, as it is enforced.
//
// Not the tier's stored requiresApproval, which no issuing path ever read and
// migration 0087 marks as superseded. A campaign always waits for a second
// person, including every one the scheduler drafts. A single vendor's
// invitation waits where the deployment says so, on every tier, or where this
// tier's own rule says so. An API older than either field says nothing about
// single invitations, so that line is left out rather than guessed.
function singleInviteWaits(tier: number, issuing: TierIssuing | null, rules: IssuingRules | null): boolean | null {
  if (rules) {
    return rules.deploymentNeedsSecondPerson || Boolean(rules.tiers.find((t) => t.tier === tier)?.needsSecondPerson)
  }
  return issuing ? issuing.singleInviteNeedsApprover : null
}

function IssuingRule({ tier, issuing, rules }: { tier: number; issuing: TierIssuing | null; rules: IssuingRules | null }) {
  const waits = singleInviteWaits(tier, issuing, rules)
  return (
    <>
      <div>Campaigns need a second person</div>
      {waits !== null && <div>{waits ? 'Single invites need a second person' : 'Single invites go straight out'}</div>}
    </>
  )
}

function TierCard({
  profile,
  issuing,
  rules,
  questionnaires,
  models,
  onSaved,
}: {
  profile: TierProfile
  issuing: TierIssuing | null
  rules: IssuingRules | null
  questionnaires: Questionnaire[]
  models: RiskModel[]
  onSaved: () => void
}) {
  const [open, setOpen] = useState(false)
  const [questionnaireId, setQuestionnaireId] = useState(profile.questionnaireId ?? '')
  const [riskModelId, setRiskModelId] = useState(profile.riskModelId ?? '')
  // Annual unless this tier already says otherwise, which is the policy
  // the requirement states rather than a blank somebody has to fill in.
  const [cadence, setCadence] = useState(profile.cadenceMonths == null ? '12' : String(profile.cadenceMonths))
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<ApiError | null>(null)
  const tierRule = rules?.tiers.find((t) => t.tier === profile.tier)
  const [secondPerson, setSecondPerson] = useState(Boolean(tierRule?.needsSecondPerson))

  // The rule can arrive after the card first drew, and the form follows it.
  useEffect(() => {
    setSecondPerson(Boolean(tierRule?.needsSecondPerson))
  }, [tierRule?.needsSecondPerson])

  function save() {
    setSaving(true)
    setSaveError(null)
    api
      .saveTierProfile(profile.tier, {
        questionnaireId: questionnaireId || undefined,
        riskModelId: riskModelId || undefined,
        // requiresApproval is not sent: it is superseded by the tier's own
        // second-person rule below, and the API keeps the stored value.
        cadenceMonths: cadence.trim() === '' ? null : Number(cadence),
      })
      // The rule is saved only when it moved, so a cadence change does not
      // put a second-person change on the trail that nobody made.
      .then(() =>
        tierRule && secondPerson !== tierRule.needsSecondPerson ? saveTierIssuing(profile.tier, secondPerson) : undefined,
      )
      .then(() => {
        setOpen(false)
        onSaved()
      })
      .catch((err) => setSaveError(asApiError(err)))
      .finally(() => setSaving(false))
  }

  // Only published questionnaires can be issued, so offering a draft here would
  // be offering a choice the campaign would later refuse.
  const publishable = questionnaires.filter((q) => q.publishedVersionId)

  return (
    <div className={profile.configured ? 'tier-card' : 'tier-card tier-unconfigured'}>
      <h3>{tierLabel(profile.tier)}</h3>

      {!profile.configured ? null : (
        <dl className="kv">
          <dt>Questionnaire</dt>
          <dd>
            {profile.questionnaireCode ? (
              <>
                <span className="mono">{profile.questionnaireCode}</span>
                <span className="cell-sub">{profile.questionnaireName}</span>
              </>
            ) : (
              <span className="muted">none</span>
            )}
          </dd>
          <dt>Risk model</dt>
          <dd>
            {profile.riskModelCode ? (
              <span className="mono">{profile.riskModelCode}</span>
            ) : (
              <span className="muted">none</span>
            )}
          </dd>
          <dt>Cadence</dt>
          <dd>
            {profile.cadenceMonths ? (
              <>every {profile.cadenceMonths} months</>
            ) : (
              <span className="muted">on change only</span>
            )}
          </dd>
          <dt>Issuing</dt>
          <dd>
            <IssuingRule tier={profile.tier} issuing={issuing} rules={rules} />
          </dd>
        </dl>
      )}

      <p className="muted">
        {profile.reassessOn === 'CHANGE_ONLY'
          ? 'Reassessed on change, nothing is issued on a timer'
          : 'Reassessed on a timer, and on change'}
      </p>

      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        {open ? 'Cancel' : profile.configured ? 'Change' : 'Configure'}
      </button>

      {open && (
        <div className="fieldset">
          {saveError && <ErrorBox error={saveError} what="The tier profile" />}
          <div className="field">
            <label htmlFor={'tp-q-' + profile.tier}>Questionnaire</label>
            <select
              id={'tp-q-' + profile.tier}
              value={questionnaireId}
              onChange={(e) => setQuestionnaireId(e.target.value)}
            >
              <option value="">None</option>
              {publishable.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.code} v{q.publishedVersionNo}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor={'tp-m-' + profile.tier}>Risk model</label>
            <select id={'tp-m-' + profile.tier} value={riskModelId} onChange={(e) => setRiskModelId(e.target.value)}>
              <option value="">None</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.code}
                  {m.activeVersionNo ? ' v' + m.activeVersionNo : ' (nothing active)'}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor={'tp-c-' + profile.tier}>Reassess</label>
            {/* A choice and not a free number. A field that took anything from
                zero to a hundred and twenty months let somebody set a cadence
                nobody would ever defend to an auditor, and annual is what the
                policy actually says. The four here are the ones a tier is
                really run on. */}
            <select
              id={'tp-c-' + profile.tier}
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              aria-describedby={'tp-c-note-' + profile.tier}
            >
              <option value="12">Once a year</option>
              <option value="6">Every six months</option>
              <option value="24">Every two years</option>
              <option value="">On change only</option>
            </select>
          </div>

          {tierRule && (
            <div className="field">
              <label className="wf-issuing-toggle">
                <input
                  type="checkbox"
                  checked={secondPerson || Boolean(rules?.deploymentNeedsSecondPerson)}
                  disabled={Boolean(rules?.deploymentNeedsSecondPerson)}
                  onChange={(e) => setSecondPerson(e.target.checked)}
                />
                <span>Single invitations wait for a second person</span>
              </label>
              <p className="field-note">
                {rules?.deploymentNeedsSecondPerson
                  ? 'This deployment already asks for a second person on every invitation.'
                  : 'Invite and Reassess now are drafted for somebody else to approve. Campaigns always are.'}
              </p>
            </div>
          )}

          <div className="commit-bar">
            <button
              type="button"
              className="primary"
              onClick={save}
              disabled={saving || !can('config.write')}
              title={can('config.write') ? undefined : refusalReason('config.write')}
              data-action="tprm.settings-reassessment.a01"
              data-action-also="tprm.settings-reassessment.a02"
            >
              {saving ? 'Saving' : 'Save ' + tierLabel(profile.tier)}
            </button>
            {!can('config.write') && (
              <p className="field-note sw-row">
                <Status tone="neutral">Read only</Status>
                <ReasonTip label="Why you cannot save" text={refusalReason('config.write')} />
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
